import { useEffect, useState } from "react";

export type ClubAccess = {
  loading: boolean;
  /** The club has no Chukkerbook subscription yet. */
  needsSubscription: boolean;
  /** The club has more players than its plan allows. */
  overPlayerLimit: boolean;
  expired: boolean;
  invalidCard: boolean;
  subscriptionEndDate: string;
  setSubscriptionEndDate: (date: string) => void;
  /** Whether the manager can use the club's schedule and event tools. */
  hasAccess: boolean;
};

type SubscriptionResponse = {
  id?: number;
  subscription_end_date?: string | null;
  allowed_players?: number;
  max_allowed_players?: number;
} | null;

function useClubAccess(devPort: string, clubId: number, enabled: boolean): ClubAccess {
  const [loading, setLoading] = useState(true);
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [overPlayerLimit, setOverPlayerLimit] = useState(false);
  const [expired, setExpired] = useState(false);
  const [invalidCard, setInvalidCard] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState("");

  useEffect(() => {
    if (!enabled || clubId < 0) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(devPort + "get_club_subscription/" + clubId, { credentials: "include" }).then(
        (response) => response.json() as Promise<SubscriptionResponse>
      ),
      fetch(devPort + "get-subscription-payment-status/" + clubId, { credentials: "include" })
        .then((response) => response.json())
        .then((data) => !!data && data["card_valid"] === false)
        .catch(() => false),
    ])
      .then(([subscription, cardInvalid]) => {
        if (cancelled) {
          return;
        }
        const hasSubscription = !!subscription && "id" in subscription;
        const endDate = (hasSubscription && subscription?.subscription_end_date) || "";
        setNeedsSubscription(!hasSubscription);
        setSubscriptionEndDate(endDate);
        setExpired(!!endDate && new Date(endDate).getTime() < Date.now());
        setOverPlayerLimit(
          hasSubscription &&
            subscription?.allowed_players !== undefined &&
            subscription?.max_allowed_players !== undefined &&
            subscription.allowed_players > subscription.max_allowed_players
        );
        setInvalidCard(cardInvalid);
      })
      .catch(() => {
        if (!cancelled) {
          setNeedsSubscription(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [devPort, clubId, enabled]);

  return {
    loading,
    needsSubscription,
    overPlayerLimit,
    expired,
    invalidCard,
    subscriptionEndDate,
    setSubscriptionEndDate,
    hasAccess: !loading && !needsSubscription && !overPlayerLimit && !expired && !invalidCard,
  };
}

export default useClubAccess;
